import { DEFAULT_YEAR } from '../../constants';
import { Filter } from '../../models';

const domesticStudyPeriods = [
  { year: DEFAULT_YEAR, studyPeriodCode: '1' },
  { year: DEFAULT_YEAR, studyPeriodCode: '2' },
  { year: DEFAULT_YEAR + 1, studyPeriodCode: '1' },
  { year: DEFAULT_YEAR + 1, studyPeriodCode: '2' },
];

const internationalStudyPeriods = [
  { year: DEFAULT_YEAR, studyPeriodCode: '1' },
  { year: DEFAULT_YEAR, studyPeriodCode: '2' },
  { year: DEFAULT_YEAR + 1, studyPeriodCode: '1' },
  { year: DEFAULT_YEAR + 1, studyPeriodCode: '2' },
  { year: DEFAULT_YEAR + 2, studyPeriodCode: '1' },
  { year: DEFAULT_YEAR + 2, studyPeriodCode: '2' },
];

const startDateFilterItem = (region: 'domestic' | 'international') => {
  const studyPeriods = region === 'domestic' ? domesticStudyPeriods : internationalStudyPeriods;
  return {
    title: 'Start date',
    region: region,
    field: 'calculations.Availabilities.StudyPeriod.code',
    metadataField: 'start_date',
    options: studyPeriods.map(({ year, studyPeriodCode }) => ({
      title: `Semester ${studyPeriodCode}, ${year}`,
      value: studyPeriodCode,
      nested: {
        path: 'calculations.Availabilities',
        filters: [
          {
            field: 'calculations.Availabilities.Year',
            value: year.toString(),
          },
        ],
      },
    }))
  }
};

export const courseFilter: Filter[] = [
  {
    title: 'Study level',
    field: 'LearningOffering.Metadata.offeringLevel.code',
    metadataField: 'study_level',
    tooltip: `<h3>Undergraduate</h3><p>A university-level course you can study after high school or equivalent – like a bachelor's or associate degree. Many undergraduate degrees include a major (your main area of study) and some offer the option to add a minor or specialisation.</p><h3>Postgraduate</h3><p>Further study after completing an undergraduate course – including graduate certificates, diplomas or master's degrees.</p><h3>Postgraduate research</h3><p>Advanced study where you'll complete your own research project – such as a PhD or MPhil.</p><h3>Bridging and enabling courses</h3><p>Introductory courses that help you meet university entry requirements and get started at Curtin.</p>
    `,
    options: [
      { title: 'Undergraduate degree', value: 'UNDERGRADUATE', metadataValue: 'undergraduate' },
      { title: 'Postgraduate degree', value: 'POSTGRADUATE', metadataValue: 'postgraduate' },
      { title: 'Research degree and doctorate', value: 'RESEARCH', metadataValue: 'research' },
      { title: 'Bridging and enabling courses', value: 'BRIDGING', metadataValue: 'bridging' },
    ],
  },
  {
    title: 'Course type',
    field: 'LearningOffering.Curriculum.Package.Category.code',
    metadataField: 'course_type',
    options: [
      { title: 'Single degree', value: ['75', '80', '85', '86', '90', '91', '105'], metadataValue: 'single-degree' },
      { title: 'Double degree', value: '81', metadataValue: 'double-degree' },
      { title: 'Undergraduate major', value: ['500', '505'], metadataValue: 'undergraduate-major' },
      { title: 'Undergraduate stream', value: ['600', '605'], metadataValue: 'undergraduate-stream' },
      {
        title: 'Undergraduate specialisation / minor',
        value: ['700', '515'],
        metadataValue: 'undergraduate-specialisation-minor',
      },
      { title: 'Undergraduate certificates', value: ['77', '78', '79'], metadataValue: 'undergraduate-certificate' },
      { title: 'Graduate certificate', value: ['112', '113'], metadataValue: 'graduate-certificate' },
      { title: 'Graduate diploma', value: '118', metadataValue: 'undergraduate-diploma' },
      { title: 'Masters', value: ['140', '142', '144', '150', '160', '170'], metadataValue: 'masters' },
      { title: 'Postgraduate major', value: '510', metadataValue: 'postgraduate-major' },
      { title: 'Doctorate (PhD)', value: ['205', '210'], metadataValue: 'doctorate' },
      //{ title: 'Bridging Course', value: 'Bridging course' }, does not exist??
      { title: 'Enabling course', value: '40', metadataValue: 'enabling-course' },
    ],
  },
  {
    title: 'Study area',
    field: 'LearningOffering.Marketing.overview.studyAreas.id',
    metadataField: 'study_area',
    options: [
      {
        title: 'Agriculture, environment and sustainability',
        value: 'agriculture-environment-and-sustainability',
        metadataValue: 'agriculture-environment-and-sustainability',
      },
      {
        title: 'Architecture and construction',
        value: 'architecture-and-construction',
        metadataValue: 'architecture-and-construction',
      },
      {
        title: 'Arts and creative industries',
        value: 'arts-and-creative-industries',
        metadataValue: 'arts-and-creative-industries',
      },
      {
        title: 'Business, innovation, management and law',
        value: 'business-management-and-law',
        metadataValue: 'business-innovation-management-and-law',
      },
      {
        title: 'Culture, society and indigenous',
        value: 'culture-language-and-indigenous',
        metadataValue: 'culture-society-and-indigenous',
      },
      { title: 'Education', value: 'education', metadataValue: 'education' },
      {
        title: 'Engineering, mining and surveying',
        value: 'engineering-and-mining',
        metadataValue: 'engineering-mining-and-surveying',
      },
      { title: 'Health', value: 'health', metadataValue: 'health' },
      { title: 'Information technology', value: 'it-and-computing', metadataValue: 'information-technology' },
      {
        title: 'Physical sciences, geoscience and mathematics',
        value: 'physical-sciences-and-mathematics',
        metadataValue: 'physical-sciences-geoscience-and-mathematics',
      },
    ],
  },
  {
    title: 'ATAR',
    type: 'slider',
    field: 'calculations.Atar.Value',
    metadataField: 'atar',
    min: 70,
    max: 100,
    step: 5,
    nested: {
      path: 'calculations.Atar',
      filters: [
        {
          field: 'calculations.Atar.Year',
          value: DEFAULT_YEAR.toString(),
          varyByYear: true,
        },
      ],
    },
  },
  {
    title: 'Attendance mode',
    region: 'domestic',
    field: 'calculations.Availabilities.DeliveryMode',
    metadataField: 'attendance_mode',
    options: [
      { title: 'Online', value: 'Online', metadataValue: 'online' },
      { title: 'On campus', value: 'Internal', metadataValue: 'on-campus' },
      { title: 'Mixed mode', value: 'Mixed', metadataValue: 'mixed-mode' },
    ],
    nested: {
      path: 'calculations.Availabilities',
      filters: [
        {
          field: 'calculations.Availabilities.Year',
          value: DEFAULT_YEAR.toString(),
          varyByYear: true,
        },
        {
          field: 'calculations.Availabilities.StudyPeriod.code',
          value: [],
          varyByStudyPeriod: true,
        },
        {
          field: 'calculations.Availabilities.Location.code',
          value: [],
          varyByLocation: true,
        },
      ],
    },
  },
  {
    title: 'Study load',
    field: 'calculations.StudyLoad.code',
    metadataField: 'study_load',
    options: [
      { title: 'Full-time', value: 'FT', metadataValue: 'full-time' },
      { title: 'Part-time', value: 'PT', metadataValue: 'part-time' },
    ],
  },
  startDateFilterItem('domestic'),
  startDateFilterItem('international')
];
